/* global YT, Vimeo */

if (!customElements.get('video-component')) {
  class Video extends HTMLElement {
    constructor() {
      super();
      this.autoplay = this.dataset.autoplay === 'true';
      this.background = this.dataset.background === 'true';
      this.naturalWidth = this.dataset.naturalWidth === 'true';
    }

    connectedCallback() {
      this.init();
    }

    init() {
      const url = this.dataset.videoUrl;

      if (url) {
        if (url.includes('youtu.be') || url.includes('youtube.com')) {
          if (url.includes('v=')) {
            this.videoId = url.split('v=').pop().split('&')[0];
          } else {
            this.videoId = url.split('?')[0].split('/').pop();
          }

          this.type = 'youtube';
          this.initYouTube();
        } else if (url.includes('vimeo.com')) {
          this.videoId = url.split('?')[0].split('/').pop();
          this.type = 'vimeo';
          this.initVimeo();
        }
      } else {
        this.type = 'tag';
        this.initVideoTag();
      }

      // Allow for videos which haven't played to play on tap
      if (this.background) {
        this.closest('.video-section').addEventListener('click', this.play.bind(this));
      }
    }

    /**
     * Loads a player API script.
     * @param {string} src - Url of script to load.
     */
    loadScript(src) {
      if (document.querySelector(`script[src="${src}"]`)) return;

      const s = document.createElement('script');
      s.src = src;
      s.onerror = (err) => console.warn(`Unable to load script ${src}`, err); // eslint-disable-line

      if (this.type === 'vimeo') {
        s.onload = () => document.dispatchEvent(new CustomEvent('on:vimeo-api:loaded'));
      } else if (this.type === 'youtube') {
        window.onYouTubeIframeAPIReady = () => document.dispatchEvent(new CustomEvent('on:youtube-api:loaded'));
      }

      document.body.appendChild(s);
    }

    /**
     * Initialises a YouTube video.
     */
    initYouTube() {
      const initYTPlayer = () => {
        YT.ready(() => {
          this.player = new YT.Player(this.querySelector('div'), {
            videoId: this.videoId,
            width: '1280',
            height: '720',
            playerVars: {
              playsinline: 1,
              controls: 0,
              autoplay: 0,
              loop: 1,
              modestbranding: 1,
              fs: 0,
              cc_load_policy: 0,
              iv_load_policy: 3,
              autohide: 1,
              playlist: this.videoId
            },
            events: {
              onReady: this.handleYTReady.bind(this),
              onStateChange: this.handleYTStateChange.bind(this)
            }
          });
        });
      };

      this.loadScript('https://www.youtube.com/iframe_api');

      if (typeof YT === 'undefined') {
        document.addEventListener('on:youtube-api:loaded', initYTPlayer.bind(this));
      } else {
        initYTPlayer();
      }
    }

    /**
     * Initialises a Vimeo video.
     */
    initVimeo() {
      const initVimeoPlayer = () => {
        this.player = new Vimeo.Player(this.querySelector('div'), {
          id: this.videoId,
          loop: true,
          muted: true,
          autoplay: false,
          controls: false,
          responsive: true,
          dnt: true,
          playsinline: true
        });

        this.player.ready().then(() => {
          this.addObserver(this.querySelector('div'));
          if (this.autoplay) this.play();
        });

        this.player.on('pause', () => {
          if (this.inViewport) this.pausedByUser = true;
        });

        this.player.on('play', () => {
          this.closest('.video-section').classList.add('video-section--played');
        });
      };

      this.loadScript('https://player.vimeo.com/api/player.js');

      if (typeof Vimeo === 'undefined') {
        document.addEventListener('on:vimeo-api:loaded', initVimeoPlayer.bind(this));
      } else {
        initVimeoPlayer();
      }
    }

    /**
     * Sets up iframe video placeholder and assigns dimensions.
     * @param {string} className - Class name for the iframe.
     */
    setupIframe(className) {
      if (!this.naturalWidth) return;

      const iframe = this.querySelector('iframe');
      if (!iframe) return;

      iframe.classList.add(className);
      iframe.setAttribute('tabindex', '-1');
      iframe.setAttribute('title', this.dataset.description || '');

      const setHeight = () => {
        const section = this.closest('.video-section');
        const container = section?.querySelector('.video-section__media') || section;
        if (!container) return;

        const w = container.offsetWidth;
        const h = container.offsetHeight;

        // Maintain 16:9 ratio while covering the container
        const ratio = 16 / 9;
        let width = w;
        let height = w / ratio;

        if (height < h) {
          height = h;
          width = h * ratio;
        }

        iframe.style.width = `${width}px`;
        iframe.style.height = `${height}px`;
      };

      setHeight();
      window.addEventListener('resize', setHeight);
    }

    /**
     * Initialises a video tag.
     */
    initVideoTag() {
      this.player = this.querySelector('video');

      this.player.addEventListener('pause', () => {
        if (this.inViewport) this.pausedByUser = true;
      });

      // If the video is already playing
      if (this.player.currentTime > 0 && !this.player.paused && !this.player.ended
        && this.player.readyState > 2) {
        this.closest('.video-section').classList.add('video-section--played');
      } else {
        this.player.addEventListener('play', () => {
          this.closest('.video-section').classList.add('video-section--played');
        });

        this.addObserver(this.player);

        // Mute/inline attributes are required for reliable autoplay on Safari/iOS
        this.player.setAttribute('playsinline', '');
        this.player.playsInline = true;

        // Mute the video if a click didn't trigger its load
        const deferredMedia = this.closest('deferred-media');
        if (!deferredMedia || deferredMedia.loadTrigger !== 'click') {
          this.player.muted = true;
          this.player.setAttribute('muted', '');
        }

        // Ensure the poster/overlay can be removed even if 'play' is delayed
        this.player.addEventListener('loadeddata', () => {
          this.closest('.video-section').classList.add('video-section--played');
        }, { once: true });

        // Avoid race conditions when inserted via <template> / deferred-media
        requestAnimationFrame(() => this.play());
      }
    }

    /**
     * Handles a YouTube ready event.
     */
    handleYTReady() {
      this.setupIframe('js-youtube');

      if (this.autoplay) this.player.mute();
      this.play();
    }

    /**
     * Handles a YouTube state change event.
     * @param {object} e - Event object.
     */
    handleYTStateChange(e) {
      if (e.data === YT.PlayerState.PAUSED) {
        if (this.inViewport) this.pausedByUser = true;
      } else if (e.data === YT.PlayerState.ENDED) {
        // Looping this way rather than 'loop' API parameter to avoid flash of black background
        if (this.background) this.player.playVideo();
      } else if (e.data === YT.PlayerState.PLAYING) {
        this.closest('.video-section').classList.add('video-section--played');
      }
    }

    /**
     * Plays the video when in view (if not paused by user).
     */
    play() {
      if (!this.player || this.pausedByUser) return;

      if (this.type === 'youtube') {
        try { this.player.playVideo(); } catch (e) {} // eslint-disable-line
        return;
      }

      if (this.type === 'vimeo') {
        // Vimeo returns a promise
        try {
          const p = this.player.play();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        } catch (e) {} // eslint-disable-line
        return;
      }

      // Native <video> (Shopify hosted)
      // Ensure autoplay requirements for Safari/iOS
      try {
        this.player.setAttribute('playsinline', '');
        this.player.playsInline = true;
        if (this.autoplay || this.background) {
          this.player.muted = true;
          this.player.setAttribute('muted', '');
        }
      } catch (e) {} // eslint-disable-line

      // If not ready yet, wait for canplay then retry once
      if (this.player.readyState < 2) {
        this.player.addEventListener('canplay', () => {
          try {
            const p = this.player.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
          } catch (e) {} // eslint-disable-line
        }, { once: true });
        return;
      }

      try {
        const p = this.player.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } catch (e) {} // eslint-disable-line
    }

    /**
     * Pauses the video when scrolled out of view (if playing).
     */
    pause() {
      if (!this.player) return;

      if (this.type === 'youtube') {
        if (this.player.getPlayerState() !== 2) {
          this.player.pauseVideo();
          this.pausedByUser = false;
        }
      } else if (this.type === 'vimeo') {
        this.player.getPaused().then((paused) => {
          if (!paused) {
            this.player.pause();
            this.pausedByUser = false;
          }
        });
      } else if (!this.player.paused) {
        this.player.pause();
        this.pausedByUser = false;
      }
    }

    /**
     * Adds an observer to pause the video when not in the viewport.
     * @param {Element} el - Element to observe.
     */
    addObserver(el) {
      if ('IntersectionObserver' in window === false) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.inViewport = true;
              this.play();
            } else {
              this.inViewport = false;
              this.pause();
            }
          });
        },
        { rootMargin: '0px 0px 200px 0px' }
      );

      observer.observe(el);
    }
  }

  customElements.define('video-component', Video);
}
